import { useMutation, useQuery } from '@apollo/client/react'
import {
  Button,
  ErrorState,
  FailureNotice,
  Icon,
  InlineConfirmation,
  LoadingState,
  Notice,
  Section,
  cn,
  useTranslate,
} from '@athena/ui'
import { adminSetUserFeatureFlagsInput } from '@athena/validation'
import { useEffect, useId, useRef, useState } from 'react'

import {
  AdminAccountDocument,
  ManagedUserDocument,
  SetManagedUserFeatureFlagsDocument,
  type FeatureFlag,
  type ManagedUserFieldsFragment,
} from '../../graphql/generated/operations.js'
import { useFirstUserErrorText } from '../../lib/user-errors.js'
import { fieldErrors, useFocusFirstInvalid } from '../../lib/validation.js'
import { ReasonField, UnsavedChanges } from './ManagementSupport.js'

/** Admin-only, audited assignment of internal capabilities to one account. */
export function UserFeatureFlags({ userId }: { userId: string }) {
  const t = useTranslate()
  const { data, loading, error, refetch } = useQuery(ManagedUserDocument, {
    variables: { userId },
    fetchPolicy: 'cache-and-network',
  })
  const user = data?.adminManagedUser

  if (!data && loading) return <LoadingState>{t('common.loading')}</LoadingState>
  if (error && !user) return <ErrorState>{t('admin.loadError')}</ErrorState>
  if (!user) return null

  return (
    <Section
      id="feature-flags"
      title={t('management.featureFlags')}
      className="border-t border-border pt-6"
    >
      <p className="max-w-prose text-body-sm text-text-secondary">
        {t('management.featureFlagsHelp')}
      </p>
      <FeatureFlagsForm key={user.id} user={user} reload={() => refetch()} />
    </Section>
  )
}

function FeatureFlagsForm({
  user,
  reload,
}: {
  user: ManagedUserFieldsFragment
  reload: () => Promise<unknown>
}) {
  const t = useTranslate()
  const userError = useFirstUserErrorText()
  const invalid = useFocusFirstInvalid<HTMLFormElement>()
  const id = useId()
  const [setFlags] = useMutation(SetManagedUserFeatureFlagsDocument, {
    refetchQueries: [ManagedUserDocument, AdminAccountDocument],
    awaitRefetchQueries: true,
  })
  // Capture the version at the first edit. A background refetch must never
  // silently rebase a draft onto a newer privileged state.
  const [draft, setDraft] = useState<{
    flags: FeatureFlag[]
    saved: FeatureFlag[]
    version: number
  } | null>(null)
  const [reason, setReason] = useState('')
  const [reasonError, setReasonError] = useState('')
  const [saved, setSaved] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [failure, setFailure] = useState<unknown>()
  const [busy, setBusy] = useState(false)
  const buttons = useRef<Partial<Record<FeatureFlag, HTMLButtonElement | null>>>({})
  const moved = useRef<FeatureFlag | null>(null)
  const flags = draft?.flags ?? user.featureFlags
  const baseline = draft?.saved ?? user.featureFlags
  const dirty = flags.length !== baseline.length || flags.some((flag) => !baseline.includes(flag))
  const stale = draft !== null && user.version !== draft.version
  const catalog = [
    {
      flag: 'PRACTICE_GENERATION_PROVIDER_OVERRIDE' as const,
      label: t('management.practiceProviderOverrideFlag'),
      help: t('management.practiceProviderOverrideFlagHelp'),
    },
    {
      flag: 'LOCAL_DEVELOPMENT' as const,
      label: t('management.localDevelopmentFlag'),
      help: t('management.localDevelopmentFlagHelp'),
    },
  ]

  useEffect(() => {
    if (moved.current) buttons.current[moved.current]?.focus()
    moved.current = null
  }, [draft])

  function toggle(flag: FeatureFlag) {
    if (busy) return
    moved.current = flag
    setDraft({
      flags: flags.includes(flag) ? flags.filter((value) => value !== flag) : [...flags, flag],
      saved: baseline,
      version: draft?.version ?? user.version,
    })
    setSaved(false)
    setMessage(null)
    setFailure(undefined)
  }

  function discard() {
    setDraft(null)
    setReason('')
    setReasonError('')
    setMessage(null)
    setFailure(undefined)
    setSaved(false)
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (busy || !dirty || stale) return
    const parsed = adminSetUserFeatureFlagsInput.safeParse({
      userId: user.id,
      version: draft?.version ?? user.version,
      flags: catalog.filter(({ flag }) => flags.includes(flag)).map(({ flag }) => flag),
      reason,
    })
    if (!parsed.success) {
      setReasonError(fieldErrors(parsed.error.issues, t)['reason'] ?? '')
      invalid.request()
      return
    }
    setBusy(true)
    setFailure(undefined)
    setMessage(null)
    setSaved(false)
    setReasonError('')
    try {
      const result = (await setFlags({ variables: { input: parsed.data } })).data
        ?.adminSetUserFeatureFlags
      if (!result?.user) {
        setMessage(userError(result?.userErrors, t('management.failed')))
        if (result?.userErrors.some(({ code }) => code === 'CONCURRENT_UPDATE')) await reload()
        return
      }
      setDraft(null)
      setReason('')
      setSaved(true)
    } catch (caught) {
      setFailure(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      ref={invalid.ref}
      noValidate
      className="flex max-w-3xl flex-col gap-5"
      onSubmit={(event) => void submit(event)}
    >
      <UnsavedChanges dirty={dirty || reason !== ''} />
      {saved ? <InlineConfirmation>{t('management.saved')}</InlineConfirmation> : null}
      {message ? <Notice tone="error" title={message} /> : null}
      {stale && !busy ? <Notice tone="warning" title={t('management.featureFlagsStale')} /> : null}
      {failure === undefined ? null : (
        <FailureNotice title={t('management.notSaved')} error={failure} />
      )}
      <fieldset disabled={busy} className="flex min-w-0 flex-col gap-5">
        {[false, true].map((enabled) => {
          const group = catalog.filter(({ flag }) => flags.includes(flag) === enabled)
          return (
            <div
              key={String(enabled)}
              role="group"
              aria-labelledby={`${id}-${enabled}`}
              className={cn(
                'flex min-w-0 flex-col gap-3',
                enabled && 'rounded-md border border-border bg-surface p-4',
              )}
            >
              <h3 id={`${id}-${enabled}`} className="text-body-sm font-semibold text-text-strong">
                {t(enabled ? 'management.enabledFeatureFlags' : 'management.availableFeatureFlags')}
              </h3>
              <div className="flex min-h-control-md flex-wrap items-start gap-2">
                {group.map(({ flag, label }) => {
                  const pending = flags.includes(flag) !== baseline.includes(flag)
                  const Glyph = enabled ? Icon.Minus : Icon.Plus
                  return (
                    <Button
                      key={flag}
                      ref={(node) => {
                        buttons.current[flag] = node
                      }}
                      variant="secondary"
                      size="sm"
                      aria-label={t(
                        enabled ? 'management.removeFeatureFlag' : 'management.addFeatureFlag',
                        [label],
                      )}
                      aria-describedby={`${id}-help-${flag}`}
                      className={cn(
                        'h-auto min-h-control-md max-w-full cursor-pointer rounded-full px-3 py-2 text-body-sm whitespace-normal max-md:min-h-touch-target-min',
                        !enabled && 'bg-surface-secondary',
                        enabled &&
                          'border-primary-border bg-primary-light text-primary-dark hover:border-primary hover:bg-primary-border',
                      )}
                      onClick={() => toggle(flag)}
                    >
                      <span className="min-w-0 text-left">{label}</span>
                      {pending ? (
                        <span className="text-caption">
                          {t(enabled ? 'management.flagToEnable' : 'management.flagToDisable')}
                        </span>
                      ) : null}
                      <Glyph className="size-icon-sm shrink-0" aria-hidden="true" />
                    </Button>
                  )
                })}
                {group.length === 0 ? (
                  <p className="self-center text-body-sm text-text-secondary">
                    {t(
                      enabled
                        ? 'management.noEnabledFeatureFlags'
                        : 'management.allFeatureFlagsSelected',
                    )}
                  </p>
                ) : null}
              </div>
              {enabled ? (
                <p aria-live="polite" className="text-caption text-text-secondary">
                  {t(
                    dirty
                      ? 'management.featureFlagsUnsaved'
                      : 'management.featureFlagsSelectionHelp',
                  )}
                </p>
              ) : null}
            </div>
          )
        })}
        <ul className="flex flex-col gap-2 text-body-sm text-text-secondary">
          {catalog.map(({ flag, label, help }) => (
            <li key={flag} id={`${id}-help-${flag}`}>
              <span className="font-semibold text-text-strong">{label}. </span>
              {help}
            </li>
          ))}
        </ul>
        <ReasonField
          value={reason}
          onChange={(value) => {
            setReason(value)
            setReasonError('')
            setSaved(false)
          }}
          error={reasonError}
        />
      </fieldset>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          type="submit"
          className="whitespace-normal"
          disabled={!dirty || stale}
          loading={busy}
          loadingText={t('common.saving')}
        >
          {t('management.saveFeatureFlags')}
        </Button>
        {dirty || reason !== '' || stale ? (
          <Button variant="ghost" className="whitespace-normal" disabled={busy} onClick={discard}>
            {t('management.resetFeatureFlags')}
          </Button>
        ) : null}
      </div>
    </form>
  )
}
